import {
  Badge,
  Button,
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  useArtifactCanvas,
} from '@yeshuman/sdk'
import { ChevronDown, ChevronUp, ListChecks, Sparkles, Trash2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'

import type { Brief } from '../../hooks/use-briefs'
import { useGenerateFramework } from '../../hooks/use-briefs'
import {
  useRemoveRoleCompetency,
  useReorderRoleCompetencies,
  useRole,
  type RoleCompetency,
  type RoleDetail,
} from '../../hooks/use-roles'
import { BiasCoachBanner, FeedbackBiasSection } from '../bias/bias-coach-banner'
import { useBiasReport } from '../../hooks/use-bias-review'
import { competencyFrameworkText, detect } from '../../lib/bias-guardrail'
import { fieldLabel, FRAMEWORK_COPY } from '../../lib/competency-framework'
import { RoleCompetencyCanvas } from './role-competency-canvas'

function moveIds(ids: string[], index: number, direction: -1 | 1): string[] | null {
  const next = index + direction
  if (next < 0 || next >= ids.length) return null
  const copy = [...ids]
  ;[copy[index], copy[next]] = [copy[next], copy[index]]
  return copy
}

function FrameworkRow({
  role,
  rc,
  index,
  total,
}: {
  role: RoleDetail
  rc: RoleCompetency
  index: number
  total: number
}) {
  const remove = useRemoveRoleCompetency()
  const reorder = useReorderRoleCompetencies()
  const onError = (error: Error) => toast.error(error.message || 'Failed to update competency')

  const move = (direction: -1 | 1) => {
    const ordered = moveIds(
      role.competencies.map((row) => row.id),
      index,
      direction,
    )
    if (!ordered) return
    reorder.mutate({ roleId: role.id, orderedIds: ordered }, { onError })
  }

  return (
    <li className="space-y-2 py-4 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <h3 className="font-medium text-foreground">{rc.competency.label}</h3>
          {rc.competency.definition ? (
            <p className="whitespace-pre-wrap text-sm text-muted-foreground">
              {rc.competency.definition}
            </p>
          ) : null}
          {rc.source_excerpt ? (
            <p className="text-sm text-foreground">
              <span className="text-muted-foreground">{FRAMEWORK_COPY.sourceLabel}</span>
              {rc.source_field ? ` · ${fieldLabel(rc.source_field)}` : ''}: {rc.source_excerpt}
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="iconSm"
            aria-label={FRAMEWORK_COPY.moveUpLabel}
            disabled={index === 0 || reorder.isPending}
            onClick={() => move(-1)}
          >
            <ChevronUp aria-hidden />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="iconSm"
            aria-label={FRAMEWORK_COPY.moveDownLabel}
            disabled={index === total - 1 || reorder.isPending}
            onClick={() => move(1)}
          >
            <ChevronDown aria-hidden />
          </Button>
          <Button
            type="button"
            variant="ghostDestructive"
            size="iconSm"
            aria-label={`${FRAMEWORK_COPY.removeLabel} ${rc.competency.label}`}
            disabled={remove.isPending}
            onClick={() =>
              remove.mutate({ roleId: role.id, rcId: rc.id }, { onError })
            }
          >
            <Trash2 aria-hidden />
          </Button>
        </div>
      </div>
      {rc.competency.skills.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {rc.competency.skills.map((skill) => (
            <Badge key={skill} variant="secondarySoft">
              {skill}
            </Badge>
          ))}
        </div>
      ) : null}
    </li>
  )
}

export function BriefCompetencyFramework({ brief }: { brief: Brief }) {
  const { openCanvas, closeCanvas } = useArtifactCanvas()
  const { role, isLoading } = useRole(brief.role_id || undefined)
  const generate = useGenerateFramework()
  const frameworkText = competencyFrameworkText(role ?? {})
  const frameworkFindings = detect(frameworkText, 'competency_framework')
  const biasReport = useBiasReport([role?.id], 'competency_framework')

  const onGenerate = (replace: boolean) => {
    generate.mutate(
      { briefId: brief.id, replace },
      {
        onSuccess: (result) => {
          toast.success(
            result.reused
              ? FRAMEWORK_COPY.reuseLead
              : `Saved ${result.role.competencies.length} competencies on ${result.role.title}`,
          )
        },
        onError: (error: Error) => toast.error(error.message || 'Could not generate a framework'),
      },
    )
  }

  const openAdd = (current: RoleDetail) => {
    openCanvas({
      title: FRAMEWORK_COPY.addLabel,
      preventAutoFocus: true,
      content: (
        <RoleCompetencyCanvas
          role={current}
          sourceBrief={brief}
          onClose={closeCanvas}
          onSaved={closeCanvas}
        />
      ),
    })
  }

  const competencies = role?.competencies ?? []
  const hasFramework = competencies.length > 0

  return (
    <Card className="group/section relative z-0">
      <CardHeader>
        <div className="flex w-full items-start justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <ListChecks className="h-5 w-5" />
              {FRAMEWORK_COPY.editorTitle}
            </CardTitle>
            <CardDescription>{FRAMEWORK_COPY.editorLead}</CardDescription>
          </div>
          <CardAction>
            <div className="flex flex-wrap justify-end gap-2">
              {role && hasFramework ? (
                <Button type="button" variant="outline" size="sm" onClick={() => openAdd(role)}>
                  {FRAMEWORK_COPY.addLabel}
                </Button>
              ) : null}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onGenerate(hasFramework)}
                disabled={generate.isPending}
              >
                <Sparkles aria-hidden />
                {generate.isPending
                  ? 'Generating…'
                  : hasFramework
                    ? FRAMEWORK_COPY.regenerateLabel
                    : FRAMEWORK_COPY.generateLabel}
              </Button>
            </div>
          </CardAction>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-16 animate-pulse rounded-md bg-muted" />
        ) : hasFramework && role ? (
          <div className="space-y-3">
            <BiasCoachBanner findings={frameworkFindings} source="edit" />
            <FeedbackBiasSection
              open={biasReport.data?.open ?? frameworkFindings}
              incidentCount={biasReport.data?.incident_count ?? 0}
            />
            <p className="text-sm text-muted-foreground">
              {FRAMEWORK_COPY.reuseLead}{' '}
              <Link
                to={`/recruiter/roles/${role.id}`}
                className="text-foreground underline-offset-4 hover:underline"
              >
                {role.title}
              </Link>
            </p>
            <ul className="divide-y divide-border">
              {competencies.map((rc, index) => (
                <FrameworkRow
                  key={rc.id}
                  role={role}
                  rc={rc}
                  index={index}
                  total={competencies.length}
                />
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{FRAMEWORK_COPY.emptyLead}</p>
        )}
      </CardContent>
    </Card>
  )
}
